import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { PackageSearch } from 'lucide-react';
import {
  useAdminSearchPlatformProductsPaginated,
  useSetPlatformProductLengthInventoryOnly,
  useSetPlatformProductPiecesPerBundle,
  useSetPlatformProductVehicleCapacity,
} from '~/api/platformProductClassification';
import { useGetTransportVehicles } from '~/api/facility';
import type { UnitCode } from '~/graphql/graphql';
import { ProductPiecesPerBundleEditor } from '~/pages/admin-inventory-classification/ProductPiecesPerBundleEditor';
import { ProductVehicleCapacityEditor } from '~/pages/admin-inventory-classification/ProductVehicleCapacityEditor';
import { useDebouncedSearchInput } from '~/hooks/useDebouncedSearchInput';
import { LabeledCheckbox } from '~/components/common/LabeledCheckbox';
import { SearchInput } from '~/components/common/SearchInput';
import { EmptyState } from '~/components/ui/EmptyState';
import { cn, toastGraphQLError } from '~/utils';

const PAGE_SIZE = 20;

export default function AdminInventoryClassification() {
  const { t } = useTranslation('inventory-classification');

  const { searchInputValue, activeSearch, handleSearchChange } =
    useDebouncedSearchInput();

  const {
    data: products,
    sentinelRef,
    isLoading,
    isLoadingMore,
    hasMoreData,
    isPlaceholderData,
    isShowingPreviousParams,
    error,
    updateItem,
  } = useAdminSearchPlatformProductsPaginated(PAGE_SIZE, activeSearch);

  // Held at page level, not per row: a row unmounts as soon as the admin types the next
  // search term, and TanStack drops mutate()-level callbacks for unmounted observers.
  const { mutate } = useSetPlatformProductLengthInventoryOnly();
  const [optimisticFlags, setOptimisticFlags] = useState<
    Record<string, boolean>
  >({});
  const [pendingLengthIds, setPendingLengthIds] = useState<ReadonlySet<string>>(
    new Set()
  );

  const withoutKey = (previous: Record<string, boolean>, key: string) => {
    if (!(key in previous)) return previous;
    const next = { ...previous };
    delete next[key];
    return next;
  };

  const withoutPending = (previous: ReadonlySet<string>, key: string) => {
    const next = new Set(previous);
    next.delete(key);
    return next;
  };

  const handleToggle = (
    productId: string,
    platformProductLengthId: string,
    checked: boolean
  ) => {
    setOptimisticFlags((previous) => ({
      ...previous,
      [platformProductLengthId]: checked,
    }));
    setPendingLengthIds((previous) =>
      new Set(previous).add(platformProductLengthId)
    );

    mutate(
      { platformProductLengthId, inventoryOnly: checked },
      {
        onSuccess: (data) => {
          // Written into the accumulated list rather than left to the invalidate, which
          // only refetches the active page — a row from an earlier page would otherwise
          // revert the moment the override is dropped.
          const updated = data.setPlatformProductLengthInventoryOnly;
          updateItem(productId, (product) => ({
            ...product,
            lengths: product.lengths.map((length) =>
              length.id === updated.id
                ? { ...length, inventoryOnly: updated.inventoryOnly }
                : length
            ),
          }));
          setOptimisticFlags((previous) =>
            withoutKey(previous, platformProductLengthId)
          );
        },
        onError: () => {
          setOptimisticFlags((previous) =>
            withoutKey(previous, platformProductLengthId)
          );
          toast.error(t('toast.error'));
        },
        onSettled: () =>
          setPendingLengthIds((previous) =>
            withoutPending(previous, platformProductLengthId)
          ),
      }
    );
  };

  const { mutateAsync: savePiecesPerBundle } =
    useSetPlatformProductPiecesPerBundle();
  const { mutateAsync: saveVehicleCapacity } =
    useSetPlatformProductVehicleCapacity();
  const {
    data: vehicleData,
    isLoading: isLoadingVehicles,
    error: vehiclesError,
  } = useGetTransportVehicles();
  const vehicles = vehicleData ? vehicleData.transportVehicles : [];

  const handlePiecesPerBundle = async (
    platformProductId: string,
    piecesPerBundle: number
  ): Promise<boolean> => {
    try {
      const data = await savePiecesPerBundle({
        platformProductId,
        piecesPerBundle,
      });
      const updated = data.setPlatformProductPiecesPerBundle;
      updateItem(platformProductId, (product) => ({
        ...product,
        piecesPerBundle: updated.piecesPerBundle,
      }));
      return true;
    } catch (error) {
      toastGraphQLError(error);
      return false;
    }
  };

  const handleVehicleCapacity = async (
    platformProductId: string,
    unit: UnitCode,
    transportVehicleId: string,
    value: number
  ): Promise<boolean> => {
    try {
      const data = await saveVehicleCapacity({
        platformProductId,
        transportVehicleId,
        capacity: { value, unit },
      });
      const updated = data.setPlatformProductVehicleCapacity;
      updateItem(platformProductId, (product) => ({
        ...product,
        vehicleCapacities: [
          ...product.vehicleCapacities.filter(
            (row) => row.transportVehicle.id !== updated.transportVehicle.id
          ),
          updated,
        ],
      }));
      return true;
    } catch (error) {
      toastGraphQLError(error);
      return false;
    }
  };

  const hasSearch = activeSearch.trim().length > 0;

  const renderBody = () => {
    if (error) {
      return (
        <p
          className="p-8 text-center text-error text-sm"
          data-testid="classification-error"
        >
          {t('results.error')}
        </p>
      );
    }

    if (!hasSearch) {
      return (
        <p className="p-8 text-center text-neut-sec text-sm">
          {t('search.prompt')}
        </p>
      );
    }

    if (isLoading && products.length === 0) {
      return (
        <p className="p-8 text-center text-neut-sec text-sm">
          {t('results.loading')}
        </p>
      );
    }

    if (products.length === 0) {
      return (
        <EmptyState
          icon={<PackageSearch size={40} />}
          title={t('results.empty_title')}
          message={t('results.empty')}
        />
      );
    }

    return (
      <>
        {vehiclesError !== null ? (
          <p
            className="px-4 py-2 text-sm text-error"
            data-testid="capacity-vehicles-error"
          >
            {t('capacity.vehicles_error')}
          </p>
        ) : null}
        {isLoadingVehicles ? (
          <p
            className="px-4 py-2 text-sm text-neut-sec"
            data-testid="capacity-loading"
          >
            {t('capacity.loading')}
          </p>
        ) : null}
        <ul
          className={cn(
            'flex flex-col gap-4',
            isPlaceholderData ? 'opacity-50' : undefined
          )}
          aria-busy={isPlaceholderData}
        >
          {products.map((product) => (
            <li
              key={product.id}
              className="rounded-lg border border-neut-border bg-neut-bg"
              data-testid="classification-product"
            >
              <div className="px-4 py-3 border-b border-neut-border">
                <p className="text-sm font-semibold text-neut-prim">
                  {product.name}
                </p>
                <p className="text-xs text-neut-sec">
                  {product.code ?? t('product.no_code')}
                </p>
                <ProductPiecesPerBundleEditor
                  productId={product.id}
                  piecesPerBundle={product.piecesPerBundle ?? null}
                  onSave={
                    isShowingPreviousParams
                      ? null
                      : (value) => handlePiecesPerBundle(product.id, value)
                  }
                />
              </div>
              <ul className="divide-y divide-neut-border">
                {product.lengths.map((length) => (
                  <li
                    key={length.id}
                    className="flex items-center justify-between px-4 py-2"
                  >
                    <span className="text-sm text-neut-prim">
                      {length.lengthDisplayValue}
                      <span className="ml-2 text-xs text-neut-400">
                        {length.isNominal
                          ? t('length.nominal')
                          : t('length.actual')}
                      </span>
                    </span>
                    <LabeledCheckbox
                      checked={
                        optimisticFlags[length.id] ?? length.inventoryOnly
                      }
                      disabled={
                        isPlaceholderData || pendingLengthIds.has(length.id)
                      }
                      onChange={(checked) =>
                        handleToggle(product.id, length.id, checked)
                      }
                      label={t('length.inventory_only')}
                      ariaLabel={t('length.inventory_only_aria', {
                        length: length.lengthDisplayValue,
                        product: product.name,
                      })}
                      testId="inventory-only-toggle"
                    />
                  </li>
                ))}
              </ul>
              <ProductVehicleCapacityEditor
                productId={product.id}
                unit={product.standardUnitsCode}
                vehicles={vehicles}
                capacities={product.vehicleCapacities}
                onSave={
                  isShowingPreviousParams
                    ? null
                    : (vehicleId, value) =>
                        handleVehicleCapacity(
                          product.id,
                          product.standardUnitsCode,
                          vehicleId,
                          value
                        )
                }
              />
            </li>
          ))}
        </ul>
        <div ref={sentinelRef} />
        {isLoadingMore && hasMoreData ? (
          <p className="p-4 text-center text-neut-sec text-sm">
            {t('results.loading_more')}
          </p>
        ) : null}
      </>
    );
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold text-neut-prim">{t('title')}</h1>
        <p className="text-sm text-neut-sec mt-1">{t('subtitle')}</p>
      </div>

      <div className="bg-neut-bg-alt rounded-lg border border-neut-border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-neut-border flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchInputValue}
            onChange={handleSearchChange}
            placeholder={t('search.placeholder')}
            ariaLabel={t('search.aria_label')}
            testId="product-search-input"
            widthClassName="min-w-[220px] max-w-md"
          />
        </div>

        <div className="p-4">{renderBody()}</div>
      </div>
    </div>
  );
}
